import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, RedirectRequest, SsoSilentRequest } from '@azure/msal-browser';
import {
  EMPTY,
  Observable,
  Subject,
  catchError,
  finalize,
  map,
  of,
  switchMap,
  takeUntil
} from 'rxjs';

import { environment } from '../environments/environment';
import { ApiService } from './api/api.service';
import { LoaderService } from './api/loader.service';
import { User } from './models/user';
import { LOGOUT_FLAG, SILENT_REDIRECT_FLAG } from './services/auth-session.constants';
import { MeService } from './services/me.service';
import { StorageService } from './services/storage.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  // While false the app is covered by the splash: the Microsoft bootstrap has not
  // decided yet whether this visitor lands on the dashboard or on the login screen,
  // and showing either one meanwhile makes the app flash between screens.
  authResolved = false;

  readonly isLoading$: Observable<boolean>;
  private authSyncInProgress = false;
  // Guards against re-attempting the silent SSO handoff more than once per load.
  private ssoAttempted = false;
  private bootstrapTimeout = 0;
  private readonly destroyed$ = new Subject<void>();

  constructor(
    private readonly loaderService: LoaderService,
    private readonly api: ApiService,
    private readonly meService: MeService,
    private readonly msalService: MsalService,
    private readonly router: Router,
    private readonly storageService: StorageService
  ) {
    this.isLoading$ = this.loaderService.activeRequests$.pipe(
      map(activeRequests => activeRequests > 0)
    );
  }

  ngOnInit(): void {
    // The public customer signature page has no seller/MSAL session: skip the
    // Microsoft auth bootstrap and the /api/me sync entirely (they only add noise
    // and a timed-out request when a seller session happens to exist in the browser).
    if (window.location.pathname.startsWith('/public/')) {
      this.authResolved = true;
      return;
    }

    // Safety net: never strand the user on the splash if the Microsoft bootstrap
    // hangs (offline, blocked endpoint). Worst case they see the login screen.
    this.bootstrapTimeout = window.setTimeout(() => {
      this.resolveAuth();
    }, 10000);

    this.msalService.handleRedirectObservable({ navigateToLoginRequestUrl: false })
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: result => {
          this.enableAccountStorageEvents();
          if (result?.account) {
            this.msalService.instance.setActiveAccount(result.account);
            this.syncMicrosoftAccount(result.account, result.accessToken);
            return;
          }
          this.syncMicrosoftAccount();
        },
        error: () => {
          // An interaction-required answer is the expected outcome of the
          // `prompt: 'none'` handoff when there is no Microsoft session: not an
          // error, just a user who has to sign in from the login screen.
          this.storageService.remove('access_token');
          this.meService.reset();
          this.resolveAuth();
        }
      });
  }

  ngOnDestroy(): void {
    window.clearTimeout(this.bootstrapTimeout);
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private syncMicrosoftAccount(
    authenticatedAccount?: AccountInfo,
    accessToken?: string
  ): void {
    if (this.authSyncInProgress) {
      return;
    }

    const account = authenticatedAccount ?? this.getActiveAccount();
    if (!account) {
      // No cached Microsoft session in this app. Before falling back to the login
      // screen, try a silent SSO handoff: the user may already be authenticated
      // against the same Entra tenant from the originating platform.
      this.attemptSsoSilent();
      return;
    }

    this.authSyncInProgress = true;
    const accessToken$ = accessToken
      ? of(accessToken)
      : this.msalService.acquireTokenSilent({
          account,
          scopes: environment.apiScopes
        }).pipe(
          map(result => result.accessToken),
          catchError(() => {
            // A cached account is not proof that a token can still be minted from
            // it: SPA refresh tokens expire within 24h, and the renewal falls back
            // to a hidden iframe that blocked third-party cookies kill. The user
            // may well still hold a Microsoft session, so ask Entra over a
            // full-page redirect instead of dropping them on the login screen.
            this.storageService.remove('access_token');
            this.meService.reset();
            this.attemptSilentRedirect(account);
            return EMPTY;
          })
        );

    accessToken$.pipe(
      switchMap(token => {
        this.storageService.set('access_token', token);
        return this.api.me.current().observable<{ data: User }>();
      }),
      catchError(() => {
        this.storageService.remove('access_token');
        this.meService.reset();
        this.resolveAuth();
        return EMPTY;
      }),
      finalize(() => {
        this.authSyncInProgress = false;
      }),
      takeUntil(this.destroyed$)
    ).subscribe(response => {
      this.meService.setCurrentUser(response.data);
      // Signed in again: a previous sign-out must not keep blocking the automatic
      // handoff for the rest of this tab's life.
      sessionStorage.removeItem(LOGOUT_FLAG);
      const currentPath = this.router.url.split(/[?#]/, 1)[0];
      if (currentPath === '/' || currentPath === '/login') {
        // Uncover only once the dashboard route is in place, so the login screen
        // underneath is never revealed.
        this.router.navigate(['/agricola']).then(() => this.resolveAuth());
        return;
      }
      this.resolveAuth();
    });
  }

  private attemptSsoSilent(): void {
    // Only try the silent handoff once per app load. On failure we intentionally
    // give up and let the user land on the login screen.
    if (this.ssoAttempted) {
      this.storageService.remove('access_token');
      this.meService.reset();
      this.resolveAuth();
      return;
    }
    this.ssoAttempted = true;

    const request: SsoSilentRequest = {
      scopes: ['openid', 'profile', ...environment.apiScopes]
    };
    // A login hint from the originating platform makes the silent SSO reliable
    // when the browser holds more than one Microsoft session.
    const loginHint = this.getLoginHint();
    if (loginHint) {
      request.loginHint = loginHint;
    }

    this.loaderService.show();
    this.msalService.ssoSilent(request)
      .pipe(
        finalize(() => this.loaderService.hide()),
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: result => {
          if (!result.account) {
            this.resolveAuth();
            return;
          }
          this.msalService.instance.setActiveAccount(result.account);
          this.syncMicrosoftAccount(result.account, result.accessToken);
        },
        error: () => {
          // The hidden-iframe handoff is unavailable whenever the browser blocks
          // third-party cookies (Safari always, Chrome/Edge with tracking
          // protection), so a failure here does not mean the user lacks a
          // Microsoft session. Retry as a full-page redirect, which does carry the
          // Entra session cookie.
          this.storageService.remove('access_token');
          this.meService.reset();
          this.attemptSilentRedirect();
        }
      });
  }

  private attemptSilentRedirect(knownAccount?: AccountInfo): void {
    // `prompt: 'none'` means Microsoft either returns straight back signed in, or
    // answers with an interaction-required error. Either way the user is never
    // asked to click anything; on error we simply land back on the login screen.
    if (!this.canAttemptSilentRedirect()) {
      this.resolveAuth();
      return;
    }
    sessionStorage.setItem(SILENT_REDIRECT_FLAG, '1');

    const request: RedirectRequest = {
      scopes: ['openid', 'profile', ...environment.apiScopes],
      prompt: 'none'
    };
    // Naming the account matters here: with several Microsoft sessions in the
    // browser, an unhinted `prompt: 'none'` is ambiguous and Entra answers with an
    // interaction-required error rather than picking one.
    const loginHint = this.getLoginHint() ?? knownAccount?.username;
    if (loginHint) {
      request.loginHint = loginHint;
    }

    // The page navigates away; the splash stays up until the browser unloads it.
    this.loaderService.show();
    this.msalService.loginRedirect(request)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        error: () => {
          this.loaderService.hide();
          this.resolveAuth();
        }
      });
  }

  private resolveAuth(): void {
    window.clearTimeout(this.bootstrapTimeout);
    this.authResolved = true;
  }

  private canAttemptSilentRedirect(): boolean {
    // Once per tab: the flag is set before navigating, so the trip back from
    // Microsoft (successful or not) can never start another round.
    if (sessionStorage.getItem(SILENT_REDIRECT_FLAG)) {
      return false;
    }
    // Someone who just signed out asked to be on the login screen.
    return !sessionStorage.getItem(LOGOUT_FLAG);
  }

  private getLoginHint(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get('login_hint') ?? params.get('username');
  }

  private getActiveAccount(): AccountInfo | null {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    const accounts = this.msalService.instance.getAllAccounts();
    const account = accounts.length > 0 ? accounts[0] : null;
    if (account) {
      this.msalService.instance.setActiveAccount(account);
    }
    return account;
  }

  private enableAccountStorageEvents(): void {
    const instance = this.msalService.instance as {
      enableAccountStorageEvents?: () => void;
    };
    instance.enableAccountStorageEvents?.();
  }
}
